/**
 * Analytics helpers.
 *
 * Two destinations, both fire-and-forget and wrapped in try/catch so they
 * NEVER break user flows:
 *   1) GA4 (existing behavior — preserved).
 *   2) Lovable Cloud `analytics_events` / `analytics_sessions` tables, used
 *      by the admin Analytics dashboard.
 *
 * Public API kept backward-compatible: `trackEvent`, `trackQuoteSubmit`,
 * `trackCtaClick` continue to work exactly as before.
 */

import { supabase } from "@/integrations/supabase/client";

type GtagWindow = Window & {
  dataLayer?: Array<Record<string, unknown> | IArguments>;
  gtag?: (...args: unknown[]) => void;
};

const SESSION_KEY = "pps_analytics_session_id";
const SESSION_INIT_KEY = "pps_analytics_session_initialized";

function uuid(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Stable session id, persisted in localStorage. Falls back to in-memory. */
let memSessionId: string | null = null;
export function getSessionId(): string {
  if (typeof window === "undefined") return memSessionId ?? (memSessionId = uuid());
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return memSessionId ?? (memSessionId = uuid());
  }
}

function detectDevice(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

function readUtm(): Record<string, string | null> {
  try {
    const sp = new URLSearchParams(window.location.search);
    return {
      utm_source: sp.get("utm_source"),
      utm_medium: sp.get("utm_medium"),
      utm_campaign: sp.get("utm_campaign"),
      utm_term: sp.get("utm_term"),
      utm_content: sp.get("utm_content"),
      gclid: sp.get("gclid"),
      fbclid: sp.get("fbclid"),
    };
  } catch {
    return {};
  }
}

function pickSource(utm: Record<string, string | null>, referrer: string): string | null {
  if (utm.utm_source) return utm.utm_source;
  if (utm.gclid) return "google_ads";
  if (utm.fbclid) return "facebook_ads";
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.replace(/^www\./, "");
      if (host && !host.includes(window.location.hostname.replace(/^www\./, ""))) {
        return host;
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

function pickMedium(utm: Record<string, string | null>, referrer: string): string | null {
  if (utm.utm_medium) return utm.utm_medium;
  if (utm.gclid) return "cpc";
  if (utm.fbclid) return "paid_social";
  if (referrer) return "referral";
  return null;
}

/**
 * Initialize / refresh the analytics session row. Idempotent — first call in
 * a tab inserts (with first_touch); subsequent calls update last_touch only.
 * Safe to call from anywhere; failures are swallowed.
 */
export async function ensureSession(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const session_id = getSessionId();
    const utm = readUtm();
    const referrer = document.referrer || "";
    const source = pickSource(utm, referrer);
    const initialized = sessionStorage.getItem(SESSION_INIT_KEY) === "1";

    const { data: userRes } = await supabase.auth.getUser();
    const user_id = userRes?.user?.id ?? null;

    if (!initialized) {
      // Plain INSERT only. Avoids the upsert ON CONFLICT path which requires
      // SELECT/UPDATE permissions that anon users may not have (those calls
      // surfaced as a 401 in the browser console and hurt the SEO/console
      // health audit). Duplicates (return code 23505) are expected on tab
      // reloads where the row already exists and are silently ignored.
      const { error } = await supabase
        .from("analytics_sessions")
        .insert({
          session_id,
          user_id,
          first_touch_source: source,
          last_touch_source: source,
          campaign: utm.utm_campaign,
          ad_set: utm.utm_content,
          device: detectDevice(),
          metadata: {
            utm,
            referrer,
            medium: pickMedium(utm, referrer),
            landing_path: window.location.pathname,
            user_agent: navigator.userAgent,
            language: navigator.language,
          },
        });
      // Mark initialized regardless — duplicate-key errors on reloads are
      // expected and any other failure should not retry-spam every nav.
      sessionStorage.setItem(SESSION_INIT_KEY, "1");
      // Suppress only expected duplicate-key noise; everything else is
      // already swallowed by the surrounding try/catch.
      void error;
    } else if (source || user_id) {
      // Light update: refresh last_touch + user link if known.
      await supabase
        .from("analytics_sessions")
        .update({
          last_touch_source: source ?? undefined,
          user_id: user_id ?? undefined,
        })
        .eq("session_id", session_id);
    }
  } catch {
    /* analytics must never throw */
  }
}

type EventOpts = {
  event_source?: "frontend" | "backend" | "system";
  request_id?: string | null;
  order_id?: string | null;
  supplier_partner_id?: string | null;
  metadata?: Record<string, unknown>;
};

/**
 * Records an event in `analytics_events`. Best-effort, async, never throws.
 * Used in addition to the GA4 dataLayer so we have a first-party record.
 */
export async function recordEvent(name: string, opts: EventOpts = {}): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const session_id = getSessionId();
    const { data: userRes } = await supabase.auth.getUser();
    await supabase.from("analytics_events").insert({
      event_name: name,
      event_source: opts.event_source ?? "frontend",
      session_id,
      user_id: userRes?.user?.id ?? null,
      request_id: opts.request_id ?? null,
      order_id: opts.order_id ?? null,
      supplier_partner_id: opts.supplier_partner_id ?? null,
      metadata: {
        path: window.location.pathname,
        ...(opts.metadata ?? {}),
      },
    });
  } catch {
    /* swallow */
  }
}

export const trackEvent = (
  name: string,
  params: Record<string, unknown> = {},
) => {
  // Enrich with standard fields so every event carries page context +
  // a consistent timestamp. These are also the fields recommended by
  // GA4 for funnel reports.
  const enriched: Record<string, unknown> = {
    ...params,
    page_location:
      params.page_location ??
      (typeof window !== "undefined" ? window.location.href : undefined),
    page_title:
      params.page_title ??
      (typeof document !== "undefined" ? document.title : undefined),
    timestamp: params.timestamp ?? new Date().toISOString(),
  };
  // GA4 / dataLayer (legacy path — preserved)
  try {
    const w = window as GtagWindow;
    if (typeof w.gtag === "function") {
      w.gtag("event", name, enriched);
    } else {
      w.dataLayer = w.dataLayer || [];
      (w.dataLayer as Array<Record<string, unknown>>).push({
        event: name,
        ...enriched,
      });
    }
  } catch {
    /* ignore */
  }
  // First-party analytics_events (additive). Fire-and-forget.
  void recordEvent(name, { metadata: enriched });
};

/**
 * RFQ submission. Emits the canonical `rfq_submit` event (marked as a
 * conversion in GA4) and keeps the legacy `quote_submitted` event so
 * historical reports continue to work.
 */
export const trackQuoteSubmit = (params: Record<string, unknown> = {}) => {
  trackEvent("rfq_submit", params);
  trackEvent("quote_submitted", params); // legacy alias — do not remove
};

/**
 * Generic CTA click. Always emits `cta_click` for backward compatibility.
 * When the label points at a pricing / RFQ CTA, ALSO emits the
 * spec-aligned `get_pricing_click` event so it can be used as a funnel
 * step in GA4 without changing every call site.
 */
const PRICING_CTA_LABELS = new Set([
  "request_quote",
  "request_quote_hero",
  "request_quote_final",
  "request_quote_bom",
  "get_pricing_featured",
  "card_get_final_price",
  "404_quote",
]);

export const trackCtaClick = (location: string, label: string) => {
  const button_text = label.replace(/_/g, " ");
  trackEvent("cta_click", { location, label, button_text });
  if (PRICING_CTA_LABELS.has(label) || label.startsWith("request_quote")) {
    trackEvent("get_pricing_click", { location, label, button_text });
  }
};
